import { useCallback } from 'react';
import { VenueBranding } from '@lunit/shared-types';

/** theme-color (панель браузера, строка состояния): фон темы заведения; у старого бэкенда — основной цвет, как раньше. */
export function brandThemeColor(config: VenueBranding): string {
  return config.themeColor || config.primaryColor;
}

/** Фавиконка: PNG 32 иконки приложения заведения (white-label), затем faviconUrl; нет — не трогаем. */
export function brandFavicon(config: VenueBranding): string | undefined {
  return config.icons?.favicon32 || config.faviconUrl || undefined;
}

/** Логотип заведения (шапка, приветствие): logoUrl или logo — бэкенд отдаёт оба; пусто — без логотипа. */
export function brandLogoUrl(config: Partial<VenueBranding> | null | undefined): string | undefined {
  const url = (config?.logoUrl || config?.logo || '').trim();
  return url || undefined;
}

export function useBranding() {
  const applyBranding = useCallback((config: VenueBranding) => {
    console.log('[applyBranding] Starting with config:', config);
    console.log('[applyBranding] ThemePreset from config:', config?.themePreset || '(not set)');
    console.log('[applyBranding] Colors from config:', {
      primaryColor: config.primaryColor,
      backgroundColor: config.backgroundColor,
      textColor: config.textColor
    });

    const root = document.documentElement;

    // NEW THEME ENGINE: Apply theme class to body (ONLY if themePreset is explicitly set)
    if (config.themePreset) {
      console.log('[applyBranding] Applying theme preset:', config.themePreset);
      const themeClass = `theme-${config.themePreset}`;

      // Remove all existing theme classes
      document.body.classList.remove('theme-vivid', 'theme-premium', 'theme-coffee', 'theme-eco', 'theme-berry');

      // Apply selected theme
      document.body.classList.add(themeClass);
      console.log('[applyBranding] Theme class applied:', themeClass);
    } else {
      // NO themePreset specified - remove ALL theme classes and use custom colors via CSS variables
      console.log('[applyBranding] No theme preset specified, using custom colors from venue settings');
      document.body.classList.remove('theme-vivid', 'theme-premium', 'theme-coffee', 'theme-eco', 'theme-berry');
    }

    // OLD SYSTEM (Deprecated): Apply colors via CSS variables for backwards compatibility
    console.log('[applyBranding] Applying legacy CSS variables (deprecated)...');
    // Apply colors
    root.style.setProperty('--brand-primary', config.primaryColor);
    root.style.setProperty('--brand-bg', config.backgroundColor);
    root.style.setProperty('--brand-text', config.textColor);
    root.style.setProperty('--brand-secondary-bg', config.secondaryBgColor);
    root.style.setProperty('--brand-accent', config.accentColor);
    root.style.setProperty('--brand-button', config.buttonColor);
    root.style.setProperty('--brand-button-text', config.buttonTextColor);

    // Additional computed colors
    root.style.setProperty('--brand-primary-text', config.buttonTextColor); // Use button text color for primary text
    root.style.setProperty('--brand-secondary', config.accentColor); // Secondary uses accent
    root.style.setProperty('--brand-muted', `${config.textColor}99`); // Muted is text color with 60% opacity (99 in hex ≈ 60%)

    console.log('[applyBranding] Legacy CSS variables applied:', {
      '--brand-primary': config.primaryColor,
      '--brand-bg': config.backgroundColor,
      '--brand-accent': config.accentColor,
    });

    // Apply font
    if (config.fontFamily) {
      root.style.setProperty('--brand-font', config.fontFamily);
    }

    // Apply border radius
    if (config.borderRadius) {
      const radiusMap = {
        none: '0',
        sm: '8px',
        md: '12px',
        lg: '16px',
        xl: '24px'
      };
      root.style.setProperty('--brand-radius', radiusMap[config.borderRadius]);
    }

    // Фавиконка заведения (новый бэкенд — PNG 32×32 своей или стандартной иконки). В Telegram безвредно.
    const favicon = brandFavicon(config);
    if (favicon) {
      updateFavicon(favicon);
    }

    // Цвет панели браузера: фон темы (themeColor), у старого бэкенда — primaryColor
    updateThemeColor(brandThemeColor(config));
  }, []);

  const updateFavicon = (url: string) => {
    let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    // Было svg Lunit (type="image/svg+xml") — с PNG-адресом браузер такую ссылку бы отбросил.
    link.type = url.toLowerCase().split('?')[0].endsWith('.svg') ? 'image/svg+xml' : 'image/png';
    link.removeAttribute('sizes');
    link.href = url;
  };

  const updateThemeColor = (color: string) => {
    let meta: HTMLMetaElement | null = document.querySelector("meta[name='theme-color']");
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = color;
  };

  return { applyBranding };
}

